// src\themes\mad\elements\Header\PrimaryDrawer.tsx
'use client';

import { ChevronRight, X } from 'lucide-react';

import Link from 'next/link';

import { Button } from '@/components/ui/button';
import { Heading } from '@/components/ui/Heading';
import { MenuItem } from '@/core/types/menuApi';
import { useDrawerStore } from '@/stores/drawerStore';
import { Settings } from '@/core/models/settings-model';
import Image from 'next/image';
import { normalizeLink } from '@/core/lib/utils';

type SidebarMenuProps = {
  mainItems: MenuItem[];
  introItems: MenuItem[];
  introDescription: string;
  mainDescription: string;
  setting: Settings;
};

export default function PrimaryDrawer({
  setting,
  mainItems,
  introItems,
  introDescription,
}: SidebarMenuProps) {
  const { primaryOpen, setPrimaryOpen, setSecondaryOpen, setSelectedItemId } =
    useDrawerStore();

  function handleClose() {
    setPrimaryOpen(false);
    setSecondaryOpen(false);
  }

  function handleItemClick(id: number) {
    setSelectedItemId(id);
    setSecondaryOpen(true);
  }

  return (
    <div
      className={`transition-transform no-scrollbar duration-300 ease-in-out bg-white overflow-y-auto pointer-events-auto fixed top-0 h-full z-50 lg:z-70 ${primaryOpen
        ? 'translate-x-0 w-full lg:w-80 ltr:border-r rtl:border-l'
        : 'ltr:-translate-x-full rtl:translate-x-full w-0 lg:w-80'
        } ltr:left-0 rtl:right-0`}
      role="navigation"
      aria-label="Primary navigation drawer"
    >
      <div className="p-4 min-h-full">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center justify-center h-full w-18">
            {setting.logo_mobile && (
              <Image
                src={String(setting.logo_mobile)} // <- اجباری
                alt={setting.site_title || 'Logo'}
                width={120}
                height={120}
                className="object-contain lg:hidden"
              />
            )}

            {setting.logo_desktop && (
              <Image
                src={String(setting.logo_desktop)}
                alt={setting.site_title || 'Logo'}
                width={140}
                height={70}
                className="object-contain hidden lg:flex"
              />
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            aria-label="Close drawer"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Main items */}
        <div className="mb-12 space-y-3">
          {mainItems.map(item => (
            <div
              key={item.id}
              className="group relative"
              onMouseEnter={() => {
                if (
                  window.innerWidth >= 1024 &&
                  item.children?.length > 0
                ) {
                  handleItemClick(item.id);
                }
              }}
            >
              {item.children && item.children.length > 0 ? (
                // حالت دارای child → یک دکمه واحد برای کل خط
                <button
                  className="w-full flex items-center justify-between min-h-13 hover:bg-primary-100 focus:bg-primary-100 rounded-md p-2 text-left group"
                  onClick={() => handleItemClick(item.id)}
                  aria-label={`Open submenu for ${item.title}`}
                >
                  <Heading level={3} className="text-xs">
                    {item.title}
                  </Heading>

                  <ChevronRight className="w-4 h-4 rtl:rotate-180 text-gray-600" />
                </button>
              ) : (
                // حالت بدون child → لینک کامل
                <Link
                  href={normalizeLink(item.link) || '#'}
                  className="flex items-center justify-between min-h-13 hover:bg-primary-100 focus:bg-primary-100 rounded-md p-2"
                  onClick={handleClose}
                >
                  <Heading level={3} className="text-xs flex-1">
                    {item.title}
                  </Heading>
                  {/* اینجا آیکون نداریم چون child ندارد */}
                </Link>
              )}
            </div>
          ))}
        </div>

        {/* Intro items */}
        <div>
          {introItems.length > 0 && (
            <div>
              {introDescription && (
                <Heading
                  level={2}
                  className="mb-6 text-sm"
                >
                  {introDescription}
                </Heading>
              )}
              <ul className="flex flex-col gap-y-1">
                {introItems.map(item => (
                  <li key={item.id} onClick={handleClose}>
                    <Link
                      href={normalizeLink(item.link) || '#'}
                      className="text-primary-600 text-xs hover:bg-primary-100 focus:bg-primary-100 rounded-md px-2 py-3 block"
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
